import { Injectable } from '@angular/core';
import { Observable, of, from, BehaviorSubject } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import {
  SynchronizationItem,
  SynchronizationOperationResult,
} from '../models/synchronization.model';
import { AdjustRecordControllerService } from '@app/api/stock-service';
import { AuditReqDTO } from '@app/api/stock-service/model/auditReqDTO';
import { AdjustDetailsReqDTO } from '@app/api/stock-service/model/adjustDetailsReqDTO';

// SynchronizationOperationResult is now exported from models

@Injectable({
  providedIn: 'root',
})
export class SynchronizationService {
  // BehaviorSubject to communicate synchronization operation results
  private synchronizationOperationResultSubject =
    new BehaviorSubject<SynchronizationOperationResult | null>(null);
  public synchronizationOperationResult$ =
    this.synchronizationOperationResultSubject.asObservable();

  constructor(
    private adjustRecordControllerService: AdjustRecordControllerService,
  ) {}

  // Method to emit synchronization operation results
  emitSynchronizationOperationResult(
    result: SynchronizationOperationResult,
  ): void {
    this.synchronizationOperationResultSubject.next(result);
  }

  // Method to clear the last result
  clearSynchronizationOperationResult(): void {
    this.synchronizationOperationResultSubject.next(null);
  }

  /**
   * Process API response that might be a Blob or JSON
   */
  private processApiResponse<T>(response: any): Observable<T> {
    if (response instanceof Blob) {
      return from(response.text()).pipe(
        map((text) => {
          try {
            return JSON.parse(text);
          } catch (error) {
            throw new Error('Failed to parse API response');
          }
        }),
      );
    }
    return of(response);
  }

  /**
   * Get synchronization records in the format expected by the list component
   */
  getSynchronizations(params: any): Observable<any> {
    const {
      warehouseCode,
      warehouseName,
      status,
      syncTime,
      page = 1,
      size = 20,
    } = params;

    // Convert page from 1-based to 0-based for API
    const apiPage = Math.max(0, page - 1);

    // Handle date range
    let startTime, endTime;
    if (syncTime && syncTime.length === 2) {
      startTime = syncTime[0];
      endTime = syncTime[1];
    }

    const pageReqDTO = {
      adjustCode: undefined,
      warehouseCode: warehouseCode || undefined,
      warehouseName: warehouseName || undefined,
      status: status || undefined,
      startTime,
      endTime,
      page: apiPage,
      size,
      sortBy: undefined,
      ascending: undefined,
    };

    return this.adjustRecordControllerService.pageAdjustRecord(pageReqDTO).pipe(
      switchMap((response) => this.processApiResponse<any>(response)),
      map((response: any) => {
        const records: SynchronizationItem[] = (response.pageContent || []).map(
          (record: any) => ({
            id: record.id || 0,
            adjustCode: record.adjustCode || '',
            warehouseCode: record.warehouseCode || '',
            warehouseName: record.warehouseName || '',
            status: record.status || 'pending',
            lastUpdateName: record.lastUpdateName || '',
            lastUpdateDate: record.lastUpdateDate || '',
            remarks: record.remarks || '',
            warehouseId: record.warehouseId || 0,
            createDate: record.createDate || '',
          }),
        );

        return {
          data: records,
          totalRecords: response.totalElements || 0,
          loading: false,
        };
      }),
      catchError((error) => {
        return of({
          data: [],
          totalRecords: 0,
          loading: false,
        });
      }),
    );
  }

  /**
   * Get synchronization by ID for editing
   */
  getSynchronizationById(id: string): Observable<SynchronizationItem | null> {
    return this.adjustRecordControllerService.getDetail2(parseInt(id)).pipe(
      switchMap((response) => this.processApiResponse<any>(response)),
      map((response: any) => {
        if (!response) return null;
        return {
          adjustCode: response.adjustCode,
          warehouseCode: response.warehouseCode,
          warehouseName: response.warehouseName,
          remarks: response.remarks,
          adjustDetailsReqDTOList: response.adjustDetails,
        };
      }),
    );
  }

  /**
   * Get detailed synchronization data for approval dialog
   */
  getSynchronizationDetailsForApproval(id: string): Observable<any> {
    return this.adjustRecordControllerService.getDetail2(parseInt(id)).pipe(
      switchMap((response) => this.processApiResponse<any>(response)),
      map((response: any) => {
        if (!response) return null;

        // Map the response to include both basic info and detailed adjustment data
        const mappedDetails = (response.adjustDetails || []).map(
          (detail: any, index: number) => ({
            id: detail.id || index + 1,
            serialNumber: index + 1,
            categoryName: detail.categoryName || '',
            spuCode: detail.spuCode || '',
            spuName: detail.spuName || '',
            skuCode: detail.skuCode || '',
            skuName: detail.skuName || '',
            adjustmentType:
              detail.type === 1
                ? 'INCREASE'
                : detail.type === 0
                  ? 'DECREASE'
                  : detail.adjustmentType,
            adjustmentQuantity:
              detail.num || detail.planNum || detail.adjustmentQuantity || 0,
            planNum: detail.planNum || 0,
            realityNum: detail.realityNum || null,
            note: detail.note || '',
          }),
        );

        return {
          id: response.id,
          adjustCode: response.adjustCode,
          warehouseCode: response.warehouseCode,
          warehouseName: response.warehouseName,
          remarks: response.remarks,
          status: response.status,
          lastUpdateName: response.lastUpdateName,
          lastUpdateDate: response.lastUpdateDate,
          adjustDetails: mappedDetails,
        };
      }),
    );
  }

  /**
   * Create new synchronization
   */
  createSynchronization(synchronizationData: any): Observable<any> {
    const createDTO = {
      adjustDetailsReqDTOList:
        synchronizationData.adjustDetailsReqDTOList || [],
      remarks: synchronizationData.remarks || '',
      warehouseCode: synchronizationData.warehouseCode,
      warehouseId: synchronizationData.warehouseId,
    };

    return this.adjustRecordControllerService.createOrUpdateAdjustRecord(
      createDTO,
    );
  }

  /**
   * Update synchronization
   */
  updateSynchronization(id: string, synchronizationData: any): Observable<any> {
    const updateDTO = {
      id: parseInt(id),
      adjustDetailsReqDTOList:
        synchronizationData.adjustDetailsReqDTOList || [],
      remarks: synchronizationData.remarks,
      warehouseCode: synchronizationData.warehouseCode,
      warehouseId: synchronizationData.warehouseId,
    };

    return this.adjustRecordControllerService.createOrUpdateAdjustRecord(
      updateDTO,
    );
  }

  /**
   * Update synchronization status (approve/reject)
   */
  synchronizationStatus(
    id: number,
    status: string,
    typeCode?: string,
    adjustDetailsReqDTOList?: AdjustDetailsReqDTO[],
  ): Observable<boolean> {
    // Build the DTO, only including optional fields if they have values
    const auditReqDTO: AuditReqDTO = {
      id: id,
      status: status,
    };

    if (typeCode != null && typeCode !== '') {
      auditReqDTO.typeCode = typeCode;
    }

    if (adjustDetailsReqDTOList != null && adjustDetailsReqDTOList.length > 0) {
      auditReqDTO.adjustDetailsReqDTOList = adjustDetailsReqDTOList;
    }

    return this.adjustRecordControllerService
      .auditAdjustRecord(auditReqDTO)
      .pipe(map(() => true));
  }

  /**
   * Batch delete synchronization records
   */
  batchDeleteSynchronizations(ids: number[]): Observable<boolean> {
    const deleteDTO = { ids };

    return this.adjustRecordControllerService.batchDelete1(deleteDTO).pipe(
      map(() => true),
      catchError((error) => {
        throw error;
      }),
    );
  }
}
